import { useEffect, useState } from "react";
import { KeyRound, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, PageHeader } from "@/components/common";

const digits = (v) => v.replace(/\D/g, "").slice(0, 6);

const PasswordForm = () => {
  const [f, setF] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("Konfirmasi password tidak sama"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: f.old_password, new_password: f.new_password });
      toast.success("Password berhasil diubah");
      setF({ old_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel space-y-5" data-testid="change-password-form">
      <span className="h-12 w-12 rounded-xl bg-primary/12 text-primary grid place-items-center"><KeyRound className="h-6 w-6" /></span>
      <Field label="Password Lama"><Input type="password" required className="h-11" value={f.old_password} onChange={set("old_password")} data-testid="old-password-input" /></Field>
      <Field label="Password Baru"><Input type="password" required minLength={8} className="h-11" value={f.new_password} onChange={set("new_password")} data-testid="new-password-input" /></Field>
      <Field label="Konfirmasi Password Baru"><Input type="password" required minLength={8} className="h-11" value={f.confirm} onChange={set("confirm")} data-testid="confirm-password-input" /></Field>
      <Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="change-password-submit">{busy ? "Menyimpan..." : "Simpan Password"}</Button>
    </form>
  );
};

const PinForm = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ password: "", pin: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.pin.length !== 6) { toast.error("PIN harus 6 angka"); return; }
    if (f.pin !== f.confirm) { toast.error("Konfirmasi PIN tidak sama"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/pin", { password: f.password, pin: f.pin });
      setUser(data); toast.success(user.has_pin ? "PIN berhasil diubah" : "PIN berhasil dibuat");
      setF({ password: "", pin: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const pinInput = (k, testId) => <Input type="password" inputMode="numeric" autoComplete="off" required className="h-11 font-mono tracking-[0.5em]" value={f[k]} onChange={(e) => setF({ ...f, [k]: digits(e.target.value) })} data-testid={testId} />;
  return (
    <form onSubmit={submit} className="panel space-y-5" data-testid="pin-form">
      <div className="flex items-center gap-3">
        <span className="h-12 w-12 rounded-xl bg-primary/12 text-primary grid place-items-center"><ShieldCheck className="h-6 w-6" /></span>
        <div><h2 className="font-heading text-lg font-bold">{user.has_pin ? "Ubah PIN" : "Buat PIN"}</h2><p className="text-sm text-muted-foreground" data-testid="pin-status">{user.has_pin ? "PIN sudah dibuat. Dipakai untuk membuka Lock Screen." : "PIN 6 angka untuk membuka Lock Screen."}</p></div>
      </div>
      <Field label="Password Akun"><Input type="password" required className="h-11" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="pin-password-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="PIN Baru (6 angka)">{pinInput("pin", "pin-input")}</Field>
        <Field label="Konfirmasi PIN">{pinInput("confirm", "pin-confirm-input")}</Field>
      </div>
      <Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="pin-submit">{busy ? "Menyimpan..." : user.has_pin ? "Simpan PIN Baru" : "Buat PIN"}</Button>
    </form>
  );
};

const LockForm = () => {
  const { user, setUser } = useAuth();
  const [idle, setIdle] = useState(String(user.lock_idle || 5));
  useEffect(() => { setIdle(String(user.lock_idle || 5)); }, [user.lock_idle]);
  const save = async (enabled, minutes) => {
    try {
      const { data } = await api.put("/auth/lock", { enabled, idle_minutes: Number(minutes) });
      setUser(data); toast.success(enabled ? "Lock Screen aktif" : "Lock Screen nonaktif");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="panel space-y-5" data-testid="lock-form">
      <div className="flex items-center gap-3">
        <span className="h-12 w-12 rounded-xl bg-primary/12 text-primary grid place-items-center"><Lock className="h-6 w-6" /></span>
        <div className="flex-1"><h2 className="font-heading text-lg font-bold">Mode Lock Screen</h2><p className="text-sm text-muted-foreground">Layar terkunci saat aplikasi dibuka dan bila lama tidak digunakan. Geser ke kanan lalu masukkan PIN.</p></div>
        <Switch checked={user.lock_enabled} disabled={!user.has_pin} onCheckedChange={(v) => save(v, idle)} data-testid="lock-toggle" />
      </div>
      {!user.has_pin && <p className="text-xs text-amber-600" data-testid="lock-need-pin">Buat PIN terlebih dahulu untuk mengaktifkan Lock Screen.</p>}
      <Field label="Kunci otomatis setelah tidak digunakan">
        <Select value={idle} onValueChange={(v) => { setIdle(v); if (user.lock_enabled) save(true, v); }}>
          <SelectTrigger className="h-11" data-testid="lock-idle-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["1", "3", "5", "10", "15", "30"].map((m) => <SelectItem key={m} value={m}>{m} menit</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </section>
  );
};

export default function PasswordPage() {
  return (
    <div className="max-w-xl space-y-6">
      <PageHeader eyebrow="Keamanan Akun" title="Ubah Password" subtitle="Gunakan minimal 8 karakter, kombinasikan huruf dan angka." />
      <PasswordForm />
      <PinForm />
      <LockForm />
    </div>
  );
}
